import React, { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";

const Header = () => {
  const [isSticky, setIsSticky] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  // Fonction spéciale pour About Us
  const handleAboutUsClick = (e) => {
    e.preventDefault();
    
    if (location.pathname === "/") {
      // Si déjà sur l'accueil, scroller vers about-us
      const aboutUsSection = document.getElementById('about-us');
      if (aboutUsSection) {
        window.scrollTo({
          top: aboutUsSection.offsetTop - 80,
          behavior: 'smooth'
        });
        window.history.pushState(null, '', '/#about-us');
      } else {
        // Si la section n'existe pas, aller en haut de l'accueil
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    } else {
      // Si sur une autre page, aller à l'accueil
      navigate('/');
      // Le scroll vers about-us se fera après le chargement
      sessionStorage.setItem('scrollToSection', 'about-us');
    }
    
    setIsMenuOpen(false);
  };

  // Fonction spéciale pour Services
  const handleServicesClick = (e) => {
    e.preventDefault();
    
    if (location.pathname === "/") {
      // Si déjà sur l'accueil, scroller vers services
      const servicesSection = document.getElementById('services');
      if (servicesSection) {
        window.scrollTo({
          top: servicesSection.offsetTop - 80,
          behavior: 'smooth'
        });
        window.history.pushState(null, '', '/#services');
      } else {
        // Si la section n'existe pas, aller en haut de l'accueil
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    } else {
      // Si sur une autre page, aller à l'accueil
      navigate('/');
      sessionStorage.setItem('scrollToSection', 'services');
    }
    
    setIsMenuOpen(false);
  };

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 100) {
        setIsSticky(true);
      } else {
        setIsSticky(false);
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <header
      className={`fixed top-0 left-0 w-full z-50 transition-all duration-300 ${
        isSticky 
          ? "bg-[#2C3E50]/95 backdrop-blur-md shadow-lg"  // Moins de padding sur mobile en sticky
          : "bg-gradient-to-b from-[#2C3E50]/85 to-transparent"  // Padding différent pour mobile/desktop
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className={`flex justify-between items-center transition-all duration-300 ${
          isSticky ? "h-16 md:h-[72px]" : "h-[72px] md:h-24"
        }`}>
          
          {/* Logo avec des tailles mieux adaptées pour mobile */}
          <div className="flex-shrink-0 mr-auto -ml-4 sm:-ml-6">
            <Link to="/" className="flex items-center" onClick={() => setIsMenuOpen(false)}>
              <img 
                src="/images/logo-type.svg"
                alt="Providence Federal Group"
                className={`w-auto max-h-full transition-all duration-300 ${
                  isSticky 
                    ? "h-[12rem] md:h-[220px]"  // Plus petit sur mobile en sticky
                    : "h-[12rem] md:h-[220px]"   // Différent pour mobile/desktop
                }`}
                style={{ 
                  width: "auto", 
                  maxWidth: isSticky ? "900px" : "900px"  // Ajusté pour mobile
                }}
              />
            </Link>
          </div>

          {/* Navigation Desktop */}
          <nav className="hidden md:flex items-center space-x-6 lg:space-x-8">
            {/* About Us - Toujours vers l'accueil */}
            <button 
              onClick={handleAboutUsClick}
              className={`text-base font-medium transition-colors duration-300 hover:text-[#C5A047] ${
                isSticky ? "text-white" : "text-white"
              }`}
            >
              About Us
            </button>
            
            {/* Services - Toujours vers l'accueil */}
            <button 
              onClick={handleServicesClick}
              className={`text-base font-medium transition-colors duration-300 hover:text-[#C5A047] ${
                isSticky ? "text-white" : "text-white"
              }`}
            >
              Services
            </button>
            
            {/* Capabilities - Page séparée */}
            <Link 
              to="/capabilities" 
              className={`text-base font-medium transition-colors duration-300 hover:text-[#C5A047] ${
                isSticky ? "text-white" : "text-white"
              }`}
              onClick={() => setIsMenuOpen(false)}
            >
              Capabilities
            </Link>
            
            {/* Leadership - Page séparée */}
            <Link 
              to="/leadership" 
              className={`text-base font-medium transition-colors duration-300 hover:text-[#C5A047] ${
                isSticky ? "text-white" : "text-white"
              }`}
              onClick={() => setIsMenuOpen(false)}
            >
              Leadership
            </Link>
            
            {/* Careers - Page séparée */}
            <Link 
              to="/career" 
              className={`text-base font-medium transition-colors duration-300 hover:text-[#C5A047] ${
                isSticky ? "text-white" : "text-white"
              }`}
              onClick={() => setIsMenuOpen(false)}
            >
              Careers
            </Link>
            
            {/* Contact - Page séparée */}
            <Link 
              to="/contact" 
              className={`text-base font-medium transition-colors duration-300 hover:text-[#C5A047] ${
                isSticky ? "text-white" : "text-white"
              }`}
              onClick={() => setIsMenuOpen(false)}
            >
              Contact
            </Link>
          </nav>

          {/* Menu Mobile */}
          <div className="md:hidden">
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className={`p-2 rounded-md transition-all duration-300 ${
                isSticky 
                  ? "text-white hover:bg-white/20" 
                  : "text-white hover:bg-white/20"
              }`}
              aria-label="Menu"
            >
              {isMenuOpen ? (
                <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              ) : (
                <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              )}
            </button>
          </div>
        </div>

        {/* Menu Mobile Dropdown */}
        {isMenuOpen && (
          <div className={`md:hidden mt-4 pb-4 rounded-xl transition-all duration-300 ${
            isSticky ? "bg-[#2C3E50] shadow-xl" : "bg-[#2C3E50]/95 backdrop-blur-sm"
          }`}>
            <div className="px-2 pt-2 space-y-3">
              <button
                onClick={handleAboutUsClick}
                className={`block w-full text-left px-4 py-3 rounded-lg font-medium transition-all duration-300 ${
                  isSticky 
                    ? "text-white hover:bg-white/10" 
                    : "text-white hover:bg-white/10"
                }`}
              >
                About Us
              </button>
              
              <button
                onClick={handleServicesClick}
                className={`block w-full text-left px-4 py-3 rounded-lg font-medium transition-all duration-300 ${
                  isSticky 
                    ? "text-white hover:bg-white/10" 
                    : "text-white hover:bg-white/10"
                }`}
              >
                Services
              </button>
              
              <Link
                to="/capabilities"
                className={`block w-full text-left px-4 py-3 rounded-lg font-medium transition-all duration-300 ${
                  isSticky 
                    ? "text-white hover:bg-white/10" 
                    : "text-white hover:bg-white/10"
                }`}
                onClick={() => setIsMenuOpen(false)}
              >
                Capabilities
              </Link>
              
              <Link
                to="/leadership"
                className={`block w-full text-left px-4 py-3 rounded-lg font-medium transition-all duration-300 ${
                  isSticky 
                    ? "text-white hover:bg-white/10" 
                    : "text-white hover:bg-white/10"
                }`}
                onClick={() => setIsMenuOpen(false)}
              >
                Leadership
              </Link>
              
              <Link
                to="/contact"
                className={`block w-full text-left px-4 py-3 rounded-lg font-medium transition-all duration-300 ${
                  isSticky 
                    ? "text-white hover:bg-white/10" 
                    : "text-white hover:bg-white/10"
                }`}
                onClick={() => setIsMenuOpen(false)}
              >
                Contact
              </Link>
            </div>
          </div>
        )}
      </div>
      
      {/* Ligne dorée décorative en bas du header sticky */}
      {isSticky && (
        <div className="absolute bottom-0 left-0 w-full h-[2px] bg-gradient-to-r from-transparent via-[#C5A047] to-transparent"></div>
      )}
    </header>
  );
};

export default Header;